import React, { ReactNode } from "react";

import { Flex, Icon, Popover, Tooltip, Typography } from "components";

type FilterChipProps = {
  label: string;
  count: number;
  isOpen: boolean;
  isDisabled?: boolean;
  onOpenChange: (open: boolean) => void;
  content: ReactNode;
  tooltip?: string;
  popoverClassName?: string;
  dataTestId?: string;
};

const SelectedOptionsCount = ({ count }: { count: number }) => (
  <Flex className="selected-filter-options-count" align="center" justify="center">
    <Typography variant="paragraph-s" bold className="margin-0">
      {count}
    </Typography>
  </Flex>
);

export const FilterChip = ({ label, count, isOpen, isDisabled = false, onOpenChange, content, tooltip, popoverClassName, dataTestId }: FilterChipProps) => (
  <Flex className={`field-filter-chip ${count > 0 ? "active" : ""} ${isDisabled ? "disabled" : ""}`}>
    <Popover
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!isDisabled) onOpenChange(open);
      }}
      trigger="click"
      placement="bottomLeft"
      content={content}
      className={`filter-chip-popover ${popoverClassName ?? ""}`}
    >
      <Flex className="field-filter-chip-trigger" align="center" gap="0.5rem" role="button" tabIndex={0} data-testid={dataTestId}>
        <Typography variant="paragraph-m" noWrap className="margin-0">
          {label}
        </Typography>
        {count > 0 &&
          (tooltip ? (
            <Tooltip placement="top" title={tooltip}>
              <SelectedOptionsCount count={count} />
            </Tooltip>
          ) : (
            <SelectedOptionsCount count={count} />
          ))}
        <Icon iconName="tabler/chevron-down" size="s" />
      </Flex>
    </Popover>
  </Flex>
);

type FilterChipOptionProps = {
  label: ReactNode;
  selected: boolean;
  onClick: () => void;
  dataTestId?: string;
};

export const FilterChipOption = ({ label, selected, onClick, dataTestId }: FilterChipOptionProps) => (
  <Flex
    className={`filter-chip-option${selected ? " selected" : ""}`}
    justify="space-between"
    align="center"
    onClick={onClick}
    role="button"
    tabIndex={0}
    data-testid={dataTestId}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
  >
    <Typography variant="paragraph-m" className="margin-0">
      {label}
    </Typography>
    {selected && <Icon iconName="tabler/circle-check" size="m-light" className="filter-chip-option-check" />}
  </Flex>
);
